import React, { useEffect, useState } from 'react';
import DeleteDataModel from './DeleteDataModel';
import { LIcon } from 'assests/GmsIcons';
import { TConnector, TField, TIndexState, TProductCategory, TSubmodel, TUpsertISRProjectDataModelsInput } from '../model';
import { CALCULATED, CONNECTOR, FRACTION } from 'Pages/GMS/ISR/ISRConstants';
import Status from 'Components/DesignSystem/Status/Status';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { FOCUS_RING_INSET } from 'Components/DesignSystem/focusRing';
import { EXPLORER_ROW, EXPLORER_ROW_STATE, EXPLORER_SELECT_BTN } from './explorerRowChrome';

type TFieldItemProps = {
    id: string;
    fieldName: string;
    fieldId:string|null|undefined;
    fieldType: string;
    isSelected: boolean;
    onSelect: () => void;
    classColor?: string;
    indexInfo: TIndexState;
    showCalculatedField?: boolean;
    onDelete: (fieldName: string|null|undefined) => void;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
}

/**
 * A field row in the Model Explorer — and a connector row too; the same component
 * renders both, which is why the type line has to strip the "Field - " prefix for
 * `Connector`.
 *
 * Restyled onto the design system 2026-08-30 — `okf/isr/e2e.md` § "The expandable
 * list". Everything above the `return` is untouched: the type-derivation effect and
 * its `[selectedItem]` dependency drive whether the fraction sub-row appears, and
 * that is behaviour, not chrome.
 */
const FieldItem = ({id, fieldName,fieldId, fieldType, onDelete, isSelected, onSelect, selectedItem, userProjectDataModels, indexInfo, showCalculatedField, classColor}: TFieldItemProps) => {
    const [name, setName] = useState<string>('');

    const handleContextMenuOptionClick = () => {
        onDelete(fieldId);
    };

    useEffect(() => {
        if (selectedItem && 'belongsToDataModel' in selectedItem && selectedItem?.belongsToDataModel) {
            setName(userProjectDataModels?.project.dataModels[indexInfo?.dataModelIndex ?? -1]?.data.fields[indexInfo?.fieldIndex ?? -1]?.type ?? "");
        } else {
            setName(userProjectDataModels?.project.fields[indexInfo?.fieldOutsideDataModelIndex ?? -1]?.data?.type ?? "");
        }
    }, [selectedItem]);

    return (
        <div id={id}>
            {/*
                The wrapper is inert now. It was `div role="button" onClick` with no
                `tabIndex` and no key handler — announced as a button, reachable by
                nobody. The select target below is a real <button>, and the ⋮ is its
                SIBLING rather than a nested `role="button"` inside it.
            */}
            <div
                id={`${id}-main`}
                data-testid={id}
                className={`${EXPLORER_ROW} ${EXPLORER_ROW_STATE(isSelected)}`}
            >
                <button
                    type="button"
                    id={`${id}-select-btn`}
                    data-testid={`${id}-select-btn`}
                    onClick={onSelect}
                    aria-current={isSelected ? 'true' : undefined}
                    className={`${EXPLORER_SELECT_BTN} ${FOCUS_RING_INSET}`}
                >
                    <div id={`${id}-content`} className='tw-flex tw-justify-start tw-items-stretch tw-w-full'>
                        {classColor ?
                            /*
                                `self-stretch`, not `h-[42px]`. Measured on a name long
                                enough to wrap: the row grew to 62px and the bar stayed
                                42px, leaving an 18px gap; and with no `shrink-0` flex
                                squeezed its 5px width down to 3.36px, so the class
                                colour was a different thickness per row.
                            */
                            <div
                                id={`${id}-color-bar`}
                                style={{backgroundColor: classColor}}
                                className="tw-w-[5px] tw-shrink-0 tw-self-stretch tw-rounded-tl-[3px] tw-rounded-bl-[3px]"
                            />
                        : null}
                        {/* `py-1` + the 16px type tokens reproduce the old 44px row exactly. */}
                        <div id={`${id}-info`} className='tw-px-3 tw-py-[5px] tw-min-w-0 tw-text-left'>
                            <Typography
                                as="div"
                                /*
                                    Weight, not colour alone (DS § 60): 500 at rest,
                                    600 when selected. Both are 12px/16px so selecting a
                                    row never reflows it. Colour is inherited from the
                                    row's state classes.
                                */
                                variant={isSelected ? 'sub-title-label' : 'sub-body-supporting'}
                                id={`${id}-name`}
                                data-testid={`${id}-name`}
                                className='tw-break-words'
                            >
                                {fieldName}
                            </Typography>
                            <div id={`${id}-type-container`} className='tw-flex tw-items-center tw-gap-2 tw-flex-wrap'>
                                {/*
                                    `ink-tertiary` (#6c778b, 4.52:1) replaces #747593,
                                    which measured 4.46:1 — under WCAG AA's 4.5:1 for
                                    body text.
                                */}
                                <Typography
                                    as="div"
                                    variant="caption-metadata"
                                    id={`${id}-type`}
                                    data-testid={`${id}-type`}
                                    className={isSelected ? '' : 'tw-text-ink-tertiary'}
                                >
                                    {(fieldType !== "Connector") ? 'Field - ' : ''}{fieldType.replace("Connector", CONNECTOR)}
                                </Typography>
                                {showCalculatedField && showCalculatedField ?
                                    /*
                                        DS `Status` — "Badge counts things; Status states
                                        things", and Calculated is a state. It is also the
                                        only DS chip that fits: `min-h-5` (20px) against
                                        Badge/Tag's `min-h-7`, which would have grown every
                                        calculated row by 10px.

                                        Replaces a `w-[67px] h-4` chip in `orange-100` /
                                        `yellow-700` — 4.30:1, under AA — whose 18px text
                                        overflowed its own 16px box by 1px top and bottom
                                        (measured). The tone pair is 6.86:1.
                                    */
                                    <Status
                                        tone="warning"
                                        label={CALCULATED}
                                        id={`${id}-calculated-badge`}
                                        data-testid={`${id}-calculated-badge`}
                                    />
                                : null}
                            </div>
                        </div>
                    </div>
                </button>
                {/*
                    `onTriggerClick={onSelect}` preserves what event bubbling used to do
                    here: opening a field row's ⋮ also selected the row. See the prop's
                    note in `DeleteDataModel`.
                */}
                <div id={`${id}-delete-menu`} className='tw-flex tw-items-center tw-pe-2'>
                    <DeleteDataModel id={id} onContextMenuOptionClick={handleContextMenuOptionClick} onTriggerClick={onSelect}/>
                </div>
            </div>

            {isSelected && name === FRACTION ?
                <div id={`${id}-fraction-details`} className='tw-flex tw-justify-start tw-w-auto tw-ms-2'>
                    <LIcon />
                    <div id={`${id}-fraction-subfield`} className='tw-mt-2 tw-rounded-ctl tw-bg-surface-sunken tw-box-border tw-w-full tw-flex tw-flex-row tw-items-center tw-justify-between tw-py-[5px] tw-px-3 tw-gap-[12px] tw-border tw-border-solid tw-border-edge tw-text-ink'>
                        <div id={`${id}-fraction-info`}>
                            {/*
                                Was #6D788C on the name (4.45:1) and #97A0AF on the type
                                (2.64:1 on white, 2.42:1 on this sunken ground) — both
                                under AA. `ink` and `ink-secondary` are 12.8:1 and 6.6:1
                                here. #6D788C is the exact value tailwind.config.js already
                                documents as broken; the `shades-of-grey-40` alias kept it.
                            */}
                            <Typography as="div" variant="sub-body-supporting" id={`${id}-fraction-name`} className='tw-italic'>
                                {fieldName} decimal
                            </Typography>
                            <Typography as="div" variant="caption-metadata" id={`${id}-fraction-type`} className='tw-italic tw-text-ink-secondary'>
                                Field - {fieldType}
                            </Typography>
                        </div>
                    </div>
                </div>
            : <div id={`${id}-empty-fraction`}></div>}
        </div>
    );
}

export default FieldItem;
